import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import LoadingComponent from './manager/loading';
import axios from 'axios';
import { useNavigation } from './NavigationContext';
import useAnalyticsTracker from './hooks/useAnalyticsTracker';

const ANALYTICS_TOKEN_ENDPOINT = process.env.REACT_APP_ANALYTICS_TOKEN_URL || 'https://api.insightos.co.uk/api/datatoken';
const ANALYTICS_REFRESH_BUFFER_MS = 60 * 1000; // refresh 1 minute before expiry
const ANALYTICS_MIN_REFRESH_MS = 15 * 1000;
const ANALYTICS_FETCH_RETRY_MS = 10 * 1000;

const decodeJwtExpiry = (token) => {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized));
    return decoded?.exp ? decoded.exp * 1000 : null;
  } catch (error) {
    console.warn('⚠️ Failed to decode analytics token expiry', error);
    return null;
  }
};

const pickAnalyticsTokenFromResponse = (data = {}) =>
  data.analyticsToken || data.token || data?.data?.analyticsToken || null;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [analyticsToken, setAnalyticsToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [booted, setBooted] = useState(false);
  const hasRedirected = useRef(false);
  const hasDbRedirected = useRef(false);
  const analyticsRefreshTimeoutRef = useRef(null);
  const lastAnalyticsFetchAttemptRef = useRef(0);
  const refreshAnalyticsTokenRef = useRef(null);

  const navigate = useNavigation();
  const location = useLocation();

  // ✅ Define public routes
const PUBLIC_ROUTES = [
  '/',
  '/paymenttrial',
  '/notetaker/',
  '/verification',        // ← also affected
  '/verification/',       // add this
  '/staffverification',
  '/staffverification/',  // add this
  '/loggedout',
  '/forgotlink',
  '/forgotpassword',
  '/invoice/',
  '/mobilelogin/',
  '/standby/',
  '/mfa'
];

  const isPublicRoutePath = (currentPath) => {
    if (!currentPath) return false;
    if (currentPath.startsWith('/invoice/')) return true;
    return PUBLIC_ROUTES.some(route => {
      if (route === '/') {
        return currentPath === '/';
      } else if (route.endsWith('/')) {
        return currentPath.startsWith(route);
      }
      return currentPath === route;
    });
  };

  const clearAuthCookie = async () => {
    try {
      await fetch('https://api.insightos.co.uk/api/logoutboot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      console.log('✅ JWT token cleared');
    } catch (error) {
      console.error('❌ Failed to clear auth cookie', error);
    }
  };

  const clearAnalyticsRefreshTimer = useCallback(() => {
    if (analyticsRefreshTimeoutRef.current) {
      clearTimeout(analyticsRefreshTimeoutRef.current);
      analyticsRefreshTimeoutRef.current = null;
    }
  }, []);

  const clearAnalyticsTokenState = useCallback(() => {
    setAnalyticsToken(null);
    clearAnalyticsRefreshTimer();
  }, [clearAnalyticsRefreshTimer]);

  const handleAnalyticsToken = useCallback(
    (token) => {
      if (!token) {
        clearAnalyticsTokenState();
        return null;
      }

      const expiresAt = decodeJwtExpiry(token);
      setAnalyticsToken(token);
      return expiresAt;
    },
    [clearAnalyticsTokenState]
  );

  const scheduleAnalyticsRefresh = useCallback(
    (expiresAtMs) => {
      clearAnalyticsRefreshTimer();
      if (!expiresAtMs) return;

      const refreshDelay = Math.max(expiresAtMs - Date.now() - ANALYTICS_REFRESH_BUFFER_MS, ANALYTICS_MIN_REFRESH_MS);
      analyticsRefreshTimeoutRef.current = setTimeout(() => {
        refreshAnalyticsTokenRef.current?.('auto');
      }, refreshDelay);
    },
    [clearAnalyticsRefreshTimer]
  );

  const refreshAnalyticsToken = useCallback(
    async (reason = 'manual') => {
      try {
        const response = await axios.get(ANALYTICS_TOKEN_ENDPOINT, {
          withCredentials: true,
        });

        const token = pickAnalyticsTokenFromResponse(response.data);
        if (!token) {
          throw new Error('Missing analyticsToken in response');
        }

        const expiresAt = handleAnalyticsToken(token);
        if (expiresAt) {
          scheduleAnalyticsRefresh(expiresAt);
        }
        return token;
      } catch (error) {
        console.error(`❌ Failed to refresh analytics token (${reason})`, error);
        clearAnalyticsTokenState();
        return null;
      }
    },
    [handleAnalyticsToken, scheduleAnalyticsRefresh, clearAnalyticsTokenState]
  );

  useEffect(() => {
    refreshAnalyticsTokenRef.current = refreshAnalyticsToken;
    return () => {
      if (refreshAnalyticsTokenRef.current === refreshAnalyticsToken) {
        refreshAnalyticsTokenRef.current = null;
      }
    };
  }, [refreshAnalyticsToken]);

  useEffect(() => () => clearAnalyticsRefreshTimer(), [clearAnalyticsRefreshTimer]);

  useEffect(() => {
    if (!user) {
      lastAnalyticsFetchAttemptRef.current = 0;
      clearAnalyticsTokenState();
      return;
    }

    if (!analyticsToken) {
      const now = Date.now();
      if (now - lastAnalyticsFetchAttemptRef.current >= ANALYTICS_FETCH_RETRY_MS) {
        lastAnalyticsFetchAttemptRef.current = now;
        refreshAnalyticsToken('bootstrap');
      }
    }
  }, [user, analyticsToken, refreshAnalyticsToken, clearAnalyticsTokenState]);

  const redirectIfNotPublic = () => {
    const currentPath = location.pathname;
    
    // Check if current route is public
    const isPublicRoute = isPublicRoutePath(currentPath);
    
    // Only redirect if not on a public route and not already redirected
    if (!isPublicRoute && !hasRedirected.current && currentPath !== '/') {
      hasRedirected.current = true;
      navigate('/');
    }
  };

  useEffect(() => {
    if (isPublicRoutePath(location.pathname)) {
      setIsVerifying(false);
      return undefined;
    }
    const checkBootStatus = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/boot-status', {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) throw new Error('Failed to check boot status');

        const data = await response.json();
        console.log('✅ Boot status:', data);

        if (data.booted === 1 && !booted) {
          setBooted(true);
          await clearAuthCookie();
          redirectIfNotPublic();
        } else if (data.booted === 0) {
          // Reset redirect flag if user is not booted and on a public route
          const currentPath = location.pathname;
          const isPublicRoute = isPublicRoutePath(currentPath);
          
          if (isPublicRoute) {
            hasRedirected.current = false;
          }
        }
      } catch (error) {
        console.error('❌ Failed to check boot status', error);
      }
    };

    const redirectToOutage = () => {
      if (hasDbRedirected.current) return;
      hasDbRedirected.current = true;
      setIsVerifying(false);
      navigate('/404');
    };

    const checkDatabaseConnection = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/db-connection', {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Database connection failed');
        }

        const data = await response.json();
        if (data?.success) {
          hasDbRedirected.current = false;
          return true;
        }

        console.warn('⚠️ Database connection response indicated failure:', data);
        redirectToOutage();
        return false;
      } catch (error) {
        console.error('❌ Database connection check failed', error);
        redirectToOutage();
        return false;
      }
    };

    const verifyToken = async () => {
      if (hasDbRedirected.current) {
        setIsVerifying(false);
        return null;
      }
      // Check if we're on a public route first
      const currentPath = location.pathname;
      const isPublicRoute = isPublicRoutePath(currentPath);

      if (isPublicRoute) {
        setIsVerifying(false);
        return null;
      }

      try {
        const response = await axios.get('https://api.insightos.co.uk/api/auth', {
          withCredentials: true,
        });

        if (response.data.success) {
          setUser(response.data.user);
          return response.data.user || null;
        } else {
          console.log('⚠️ Invalid or missing token');
          setUser(null);
          clearAnalyticsTokenState();
          // Never force-logout while on MFA route; temp auth can still be in-flight.
          if (!isPublicRoute && location.pathname !== '/loggedout' && !location.pathname.startsWith('/mfa') && !hasDbRedirected.current) {
            navigate('/loggedout');
          }
          return null;
        }
      } catch (error) {
        console.error('❌ Token verification failed', error);
        setUser(null);
        clearAnalyticsTokenState();
        // Never force-logout while on MFA route; temp auth can still be in-flight.
        if (!isPublicRoute && location.pathname !== '/loggedout' && !location.pathname.startsWith('/mfa') && !hasDbRedirected.current) {
          navigate('/loggedout');
        }
        return null;
      } finally {
        if (!hasDbRedirected.current) {
          setIsVerifying(false);
        }
      }
    };

    const runProtectedChecks = async () => {
      const dbHealthy = await checkDatabaseConnection();
      if (!dbHealthy) {
        return;
      }

      // Verify auth token first so temp MFA sessions are recognized.
      const verifiedUser = await verifyToken();

      // Only run boot-status enforcement for full sessions, never for temp MFA sessions.
      if (verifiedUser && !verifiedUser.temp && !verifiedUser.mfaRequired) {
        await checkBootStatus();
      }
    };

    // ✅ Initial check
    runProtectedChecks();

    // 🔁 Recheck every 60 seconds
    const intervalId = setInterval(() => {
      console.log('🔁 Checking boot status...');
      runProtectedChecks();
    }, 60000);

    return () => clearInterval(intervalId);
  }, [booted, location.pathname, clearAnalyticsTokenState]);

  useAnalyticsTracker({
    companyCode: user?.companyCode,
    endpoint: process.env.REACT_APP_ANALYTICS_URL || 'https://analytics.insightos.co.uk/data/bucket',
    analyticsToken,
    enabled: Boolean(user),
  });

  if (isVerifying) {
    return <LoadingComponent />;
  }

  const authValue = user ? { ...user, analyticsToken, refreshAnalyticsToken } : null;

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}
